import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency, COMMON_CURRENCIES } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyLine = () => ({
  product_id: null,
  name: "",
  description: "",
  unit: "",
  quantity: 1,
  unit_price: 0,
  tax_rate: 0,
  discount_percent: 0,
});

function lineComputed(li) {
  const qty = Number(li.quantity) || 0;
  const price = Number(li.unit_price) || 0;
  const disc = Number(li.discount_percent) || 0;
  const tax = Number(li.tax_rate) || 0;
  const base = qty * price;
  const discount = base * (disc / 100);
  const subtotal = base - discount;
  const taxAmount = subtotal * (tax / 100);
  return {
    subtotal: +subtotal.toFixed(2),
    discount: +discount.toFixed(2),
    tax: +taxAmount.toFixed(2),
    total: +(subtotal + taxAmount).toFixed(2),
  };
}

export default function InvoiceForm() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { company } = useAuth();
  const isEdit = !!id;
  const language = i18n.language?.split("-")[0] || "en";

  const [form, setForm] = useState({
    customer_id: "",
    type: "standard",
    currency: company?.default_currency || "SEK",
    issue_date: todayIso(),
    due_date: todayIso(),
    line_items: [emptyLine()],
    notes: "",
    footer: "",
    pdf_language: language,
  });

  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  // Load reference lists
  useEffect(() => {
    (async () => {
      try {
        const [c, p] = await Promise.all([
          api.get("/customers?is_active=true&page_size=100"),
          api.get("/products?is_active=true&page_size=100"),
        ]);
        setCustomers(c.data.items);
        setProducts(p.data.items);
      } catch (err) {
        toast.error(formatApiError(err, t("invoices.toasts.error")));
      }
    })();
  }, [t]);

  // Load existing for edit
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${id}`);
        if (data.status !== "draft") {
          toast.error(t("invoices.toasts.cannotEditNonDraft"));
          navigate(`/app/invoices/${id}`, { replace: true });
          return;
        }
        setForm({
          customer_id: data.customer.customer_id,
          type: data.type,
          currency: data.currency,
          issue_date: data.issue_date,
          due_date: data.due_date,
          line_items: data.line_items.map((li) => ({
            product_id: li.product_id,
            name: li.name,
            description: li.description || "",
            unit: li.unit || "",
            quantity: li.quantity,
            unit_price: li.unit_price,
            tax_rate: li.tax_rate,
            discount_percent: li.discount_percent || 0,
          })),
          notes: data.notes || "",
          footer: data.footer || "",
          pdf_language: data.pdf_language || language,
        });
      } catch (err) {
        toast.error(formatApiError(err, t("invoices.toasts.error")));
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id, language, navigate, t]);

  const set = useCallback((field, v) => setForm((f) => ({ ...f, [field]: v })), []);

  function setLine(idx, patch) {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.map((li, i) => (i === idx ? { ...li, ...patch } : li)),
    }));
  }
  function addLine() {
    setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  }
  function removeLine(idx) {
    setForm((f) => ({
      ...f,
      line_items: f.line_items.filter((_, i) => i !== idx),
    }));
  }
  function pickProduct(idx, productId) {
    if (productId === "__adhoc") {
      setLine(idx, { product_id: null });
      return;
    }
    const p = products.find((x) => x.id === productId);
    if (!p) return;
    setLine(idx, {
      product_id: p.id,
      name: p.name,
      description: p.description || "",
      unit: p.unit || "",
      unit_price: p.price,
      tax_rate: p.tax_rate,
    });
  }

  // Auto-default currency from selected customer
  useEffect(() => {
    if (!form.customer_id) return;
    const c = customers.find((x) => x.id === form.customer_id);
    if (!c) return;
    setForm((f) => {
      const next = { ...f };
      if (!isEdit) {
        next.currency = c.currency;
        next.pdf_language = c.preferred_language || f.pdf_language;
        const due = new Date(f.issue_date);
        due.setDate(due.getDate() + (c.default_payment_terms_days || 30));
        next.due_date = due.toISOString().slice(0, 10);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.customer_id, customers]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let discount = 0;
    let tax = 0;
    let total = 0;
    for (const li of form.line_items) {
      const c = lineComputed(li);
      subtotal += c.subtotal;
      discount += c.discount;
      tax += c.tax;
      total += c.total;
    }
    return {
      subtotal: +subtotal.toFixed(2),
      discount: +discount.toFixed(2),
      tax: +tax.toFixed(2),
      total: +total.toFixed(2),
    };
  }, [form.line_items]);

  async function save(thenSend = false) {
    if (!form.customer_id) {
      toast.error(t("invoices.errors.customerRequired"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_id: form.customer_id,
        type: form.type,
        currency: form.currency,
        issue_date: form.issue_date,
        due_date: form.due_date,
        line_items: form.line_items.filter((li) => li.name && Number(li.quantity) > 0),
        notes: form.notes || null,
        footer: form.footer || null,
        pdf_language: form.pdf_language,
      };
      let saved;
      if (isEdit) {
        const { data } = await api.patch(`/invoices/${id}`, payload);
        saved = data;
        toast.success(t("invoices.toasts.updated"));
      } else {
        const { data } = await api.post("/invoices", payload);
        saved = data;
        toast.success(t("invoices.toasts.created"));
      }
      if (thenSend) {
        const { data } = await api.post(`/invoices/${saved.id}/send`);
        saved = data;
        toast.success(t("invoices.toasts.sent"));
      }
      navigate(`/app/invoices/${saved.id}`, { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 429 && d?.error === "quota_exceeded") {
        toast.error(t("quota.modalBody", { cap: d.cap || 10 }), {
          action: { label: t("quota.upgradeCta"), onClick: () => { window.location.href = "mailto:info@nordicscandiskroyal.se?subject=InvoicePro%20Upgrade"; } },
          duration: 8000,
        });
      } else {
        toast.error(formatApiError(err, t("invoices.toasts.error")));
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-500" data-testid="invoice-form-loading">{t("common.loading")}</div>;
  }

  return (
    <div className="space-y-8" data-testid="invoice-form-page">
      <header className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/app/invoices")}
            data-testid="invoice-form-back"
            className="gap-1"
          >
            <ArrowLeft className="h-4 w-4 rtl:-scale-x-100" />
            {t("common.back")}
          </Button>
          <h1 className="font-heading text-3xl tracking-tight text-slate-900">
            {isEdit ? t("invoices.edit.title") : t("invoices.create.title")}
          </h1>
        </div>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-2 sm:col-span-2">
          <Label>{t("invoices.form.customer")}</Label>
          <Select value={form.customer_id} onValueChange={(v) => set("customer_id", v)}>
            <SelectTrigger data-testid="invoice-form-customer">
              <SelectValue placeholder={t("invoices.form.customerPlaceholder")} />
            </SelectTrigger>
            <SelectContent>
              {customers.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name} · {c.currency}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("invoices.form.type")}</Label>
          <Select value={form.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger data-testid="invoice-form-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">{t("invoices.type.standard")}</SelectItem>
              <SelectItem value="cash">{t("invoices.type.cash")}</SelectItem>
              <SelectItem value="credit">{t("invoices.type.credit")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("invoices.form.currency")}</Label>
          <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
            <SelectTrigger data-testid="invoice-form-currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COMMON_CURRENCIES.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t("invoices.form.issueDate")}</Label>
          <Input
            type="date"
            value={form.issue_date}
            onChange={(e) => set("issue_date", e.target.value)}
            data-testid="invoice-form-issue"
          />
        </div>
        <div className="space-y-2">
          <Label>{t("invoices.form.dueDate")}</Label>
          <Input
            type="date"
            value={form.due_date}
            onChange={(e) => set("due_date", e.target.value)}
            data-testid="invoice-form-due"
          />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>{t("invoices.form.pdfLanguage")}</Label>
          <Select value={form.pdf_language} onValueChange={(v) => set("pdf_language", v)}>
            <SelectTrigger data-testid="invoice-form-pdflang">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORTED_LANGUAGES.map((lng) => (
                <SelectItem key={lng} value={lng}>
                  {t(`languages.${lng}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </section>

      <section className="space-y-3" data-testid="invoice-form-lines">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg text-slate-900">
            {t("invoices.form.lineItems")}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={addLine}
            data-testid="invoice-form-addline"
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            {t("invoices.form.addLine")}
          </Button>
        </div>

        <div className="space-y-3">
          {form.line_items.map((li, idx) => {
            const c = lineComputed(li);
            return (
              <div
                key={idx}
                className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-3 sm:grid-cols-12"
                data-testid={`invoice-line-${idx}`}
              >
                <div className="sm:col-span-3">
                  <Label className="text-xs">{t("invoices.form.product")}</Label>
                  <Select
                    value={li.product_id || "__adhoc"}
                    onValueChange={(v) => pickProduct(idx, v)}
                  >
                    <SelectTrigger data-testid={`invoice-line-${idx}-product`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__adhoc">
                        {t("invoices.form.adhoc")}
                      </SelectItem>
                      {products
                        .filter((p) => !p.currency || p.currency === form.currency)
                        .map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs">{t("invoices.form.lineName")}</Label>
                  <Input
                    value={li.name}
                    onChange={(e) => setLine(idx, { name: e.target.value })}
                    data-testid={`invoice-line-${idx}-name`}
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-xs">{t("invoices.form.qty")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={li.quantity}
                    onChange={(e) => setLine(idx, { quantity: e.target.value })}
                    data-testid={`invoice-line-${idx}-qty`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">{t("invoices.form.unitPrice")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    value={li.unit_price}
                    onChange={(e) => setLine(idx, { unit_price: e.target.value })}
                    data-testid={`invoice-line-${idx}-price`}
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-xs">{t("invoices.form.tax")}%</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={li.tax_rate}
                    onChange={(e) => setLine(idx, { tax_rate: e.target.value })}
                    data-testid={`invoice-line-${idx}-tax`}
                  />
                </div>
                <div className="sm:col-span-1">
                  <Label className="text-xs">{t("invoices.form.discount")}%</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={li.discount_percent}
                    onChange={(e) => setLine(idx, { discount_percent: e.target.value })}
                    data-testid={`invoice-line-${idx}-discount`}
                  />
                </div>
                <div className="flex items-end justify-end gap-2 sm:col-span-1">
                  <div className="text-end text-sm font-medium tabular-nums text-slate-900">
                    {formatCurrency(c.total, form.currency, language)}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeLine(idx)}
                    data-testid={`invoice-line-${idx}-remove`}
                    className="text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="sm:col-span-12">
                  <Textarea
                    rows={1}
                    placeholder={t("invoices.form.lineDescription")}
                    value={li.description}
                    onChange={(e) => setLine(idx, { description: e.target.value })}
                    data-testid={`invoice-line-${idx}-desc`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-3">
          <div className="space-y-2">
            <Label>{t("invoices.form.notes")}</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              data-testid="invoice-form-notes"
            />
          </div>
          <div className="space-y-2">
            <Label>{t("invoices.form.footer")}</Label>
            <Textarea
              rows={2}
              value={form.footer}
              onChange={(e) => set("footer", e.target.value)}
              data-testid="invoice-form-footer"
            />
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.subtotal")}</span><span className="tabular-nums">{formatCurrency(totals.subtotal, form.currency, language)}</span></div>
            {totals.discount > 0 && (
              <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.discount")}</span><span className="tabular-nums">− {formatCurrency(totals.discount, form.currency, language)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">{t("invoices.totals.tax")}</span><span className="tabular-nums">{formatCurrency(totals.tax, form.currency, language)}</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-2 font-semibold text-slate-900"><span>{t("invoices.totals.total")}</span><span className="tabular-nums" data-testid="invoice-form-grandtotal">{formatCurrency(totals.total, form.currency, language)}</span></div>
          </div>
        </div>
      </section>

      <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-6">
        <Button
          variant="outline"
          onClick={() => navigate("/app/invoices")}
          data-testid="invoice-form-cancel"
        >
          {t("common.cancel")}
        </Button>
        <Button
          variant="outline"
          onClick={() => save(false)}
          disabled={submitting}
          data-testid="invoice-form-save-draft"
        >
          {t("invoices.form.saveDraft")}
        </Button>
        <Button
          onClick={() => save(true)}
          disabled={submitting}
          data-testid="invoice-form-save-send"
          className="bg-slate-900 text-white hover:bg-slate-800"
        >
          {t("invoices.form.saveAndSend")}
        </Button>
      </footer>
    </div>
  );
}
